import React from 'react';
import Api from '../../api';
import { trackCeDirectoryEvent } from './analytics';
import {
  CE_ELIGIBILITY_DISCLAIMER,
  FORMAT_LABELS,
  formatDirectoryLocation,
  formatDirectorySchedule,
  getAudienceLabel,
} from './display';
import { openCeDirectoryUrl } from './externalNavigation';
import type {
  CeDirectoryFilters,
  CeDirectoryListing,
  CeDirectoryScope,
  CeDirectorySourceSurface,
} from './types';
import styles from './CeDirectory.module.scss';

type Props = {
  listing: CeDirectoryListing;
  filters: CeDirectoryFilters;
  effectiveScope: CeDirectoryScope;
  sourceSurface: CeDirectorySourceSurface;
  locationId?: number;
};

const CeDirectoryCard: React.FC<Props> = ({
  listing,
  filters,
  effectiveScope,
  sourceSurface,
  locationId,
}) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const [logoFailed, setLogoFailed] = React.useState(false);
  const [recognitionLogoFailed, setRecognitionLogoFailed] = React.useState(false);
  const [clickPending, setClickPending] = React.useState(false);
  const [clickError, setClickError] = React.useState<string | null>(null);
  const [webFallbackUrl, setWebFallbackUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    setImageFailed(false);
    setLogoFailed(false);
    setRecognitionLogoFailed(false);
    setClickError(null);
    setWebFallbackUrl(null);
  }, [listing.id]);

  const schedule = formatDirectorySchedule(listing);
  const location = formatDirectoryLocation(listing);
  const positiveCe = listing.ce.points > 0;
  const recognition = positiveCe ? listing.ce.recognition : null;

  const handleViewCourse = async () => {
    if (clickPending) return;
    setClickPending(true);
    setClickError(null);
    setWebFallbackUrl(null);
    try {
      const response = await Api.createCeListingClick(listing.id, sourceSurface, locationId);
      if (!openCeDirectoryUrl(response?.url)) {
        throw new Error('The course link could not be opened.');
      }
      if (typeof (window as any).cordova?.InAppBrowser?.open !== 'function') {
        setWebFallbackUrl(response.url);
      }
      try {
        trackCeDirectoryEvent('CE Directory Listing Clicked', filters, {
          listing,
          effectiveScope,
        });
      } catch (analyticsError) {
        console.warn('CE directory click analytics failed', analyticsError);
      }
    } catch (_error) {
      setClickError('We could not open this course. Please try again.');
    } finally {
      setClickPending(false);
    }
  };

  return (
    <article className={styles.card} data-listing-id={listing.id}>
      <div className={styles.courseImage}>
        {listing.courseImageUrl && !imageFailed ? (
          <img
            src={listing.courseImageUrl}
            alt=""
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className={styles.courseImageFallback} aria-hidden="true">CE</div>
        )}
      </div>

      <div className={styles.cardBody}>
        <div className={styles.providerRow}>
          {listing.provider.logoUrl && !logoFailed ? (
            <img
              className={styles.providerLogo}
              src={listing.provider.logoUrl}
              alt={`${listing.provider.name} logo`}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <span className={styles.providerLogoFallback} aria-hidden="true">
              {listing.provider.name.charAt(0).toUpperCase() || 'C'}
            </span>
          )}
          <span>{listing.provider.name}</span>
        </div>

        <h3 className={styles.cardTitle}>{listing.title}</h3>
        {listing.description && <p className={styles.description}>{listing.description}</p>}

        <div className={styles.tags} aria-label="Course details">
          <span>{listing.category.label}</span>
          {listing.category.group && <span>{listing.category.group.replace(/_/g, ' ')}</span>}
          {listing.audienceTags.map(audience => (
            <span key={audience}>{getAudienceLabel(audience)}</span>
          ))}
        </div>

        <dl className={styles.details}>
          <div><dt>Format</dt><dd>{FORMAT_LABELS[listing.format]}</dd></div>
          {schedule && <div><dt>Date & time</dt><dd>{schedule}</dd></div>}
          {location && <div><dt>Location</dt><dd>{location}</dd></div>}
          <div><dt>Price</dt><dd>{listing.priceDisplayText || 'See provider for pricing'}</dd></div>
        </dl>

        <section className={styles.ceBlock} aria-label="Continuing education information">
          <strong>{positiveCe ? `CE Points: ${listing.ce.points}` : 'No CE Points'}</strong>
          {positiveCe && !recognition && <p>{listing.ce.disclosure}</p>}
          {recognition && (
            <div className={styles.recognition}>
              <div className={styles.recognitionHeading}>
                {recognition.logoUrl && !recognitionLogoFailed && (
                  <img
                    src={recognition.logoUrl}
                    alt={`${recognition.label} logo`}
                    onError={() => setRecognitionLogoFailed(true)}
                  />
                )}
                <span>{recognition.label}</span>
              </div>
              <p>{recognition.officialStatement}</p>
              <p>{recognition.requiredDisclosure}</p>
            </div>
          )}
          <p className={styles.eligibilityDisclaimer}>{CE_ELIGIBILITY_DISCLAIMER}</p>
        </section>

        <button
          type="button"
          className={styles.viewButton}
          onClick={handleViewCourse}
          disabled={clickPending}
        >
          {clickPending ? 'Opening…' : 'View course ↗'}
        </button>
        {clickError && <p className={styles.clickError} role="alert">{clickError}</p>}
        {webFallbackUrl && (
          <p className={styles.linkFallback}>
            No new tab?{' '}
            <a href={webFallbackUrl} target="_blank" rel="noopener noreferrer">Open course ↗</a>
          </p>
        )}
      </div>
    </article>
  );
};

export default CeDirectoryCard;
